import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { ExperimentProviderContext } from '@carvana/experiment';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useMemo, useState } from 'react';
import type { Experiments } from '~/contracts/common';
import { logError } from '~/utilities/loggers';

export default function useEventExperiments(
  browserCookieIdExperiments: Partial<Experiments>,
  userIdExperiments: Partial<Experiments>
) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { experiments } = useContext(ExperimentProviderContext);

  const [experimentsLoaded, setExperimentsLoaded] = useState(false);
  const [forceLoaded, setForceLoaded] = useState(false);

  const experimentNames = useMemo(
    () =>
      Object.keys({
        ...browserCookieIdExperiments,
        ...userIdExperiments
      }),
    [browserCookieIdExperiments, userIdExperiments]
  );

  const experimentsWithBucket = useMemo(
    () =>
      experimentNames.reduce<Record<string, string | null>>(
        (acc, experimentName) => {
          acc[experimentName] =
            experiments[experimentName]?.assignedBucket ?? null;

          return acc;
        },
        {}
      ),
    [experimentNames, experiments]
  );

  const loadedExperiments = useMemo(
    () =>
      Object.entries(experimentsWithBucket)
        .filter(([_, bucket]) => bucket != null)
        .map(([experimentName]) => experimentName),
    [experimentsWithBucket]
  );

  const didLoad =
    loadedExperiments.length === experimentNames.length || forceLoaded;

  useEffect(() => {
    const t = window.setTimeout(() => {
      const unloadedExperiments = Object.entries(experimentsWithBucket)
        .filter(([_, bucket]) => bucket == null)
        .map(([experimentName]) => experimentName);

      logError(new Error('Experiments failed to load'), {
        unloadedExperiments
      });
      setForceLoaded(true);
    }, 10000);

    if (didLoad) {
      window.clearTimeout(t);
      setExperimentsLoaded(true);
    }

    return () => window.clearTimeout(t);
  }, [didLoad, experimentsWithBucket, setExperimentsLoaded]);

  useEffectOnce(
    () => {
      datadogRum.setGlobalContextProperty('experiments', experimentsWithBucket);

      trackEvent('experiments', { experiments: experimentsWithBucket });
    },
    [experimentsWithBucket, trackEvent],
    experimentsLoaded
  );
}
